import { useMemo, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  Bell,
  Blocks,
  Boxes,
  CalendarClock,
  ChartNoAxesCombined,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  CircleHelp,
  ClipboardList,
  FileArchive,
  FileCheck2,
  GitBranch,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Search,
  Settings2,
  ShieldAlert,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext';
import { formatDate } from '../lib/format';
import { Avatar, ErrorState, LoadingState, Modal, PermissionState, cx } from './ui';

interface NavItem {
  label: string;
  shortLabel: string;
  path: string;
  icon: LucideIcon;
  count?: number;
  group?: 'control' | 'operate' | 'assure';
}

const navItems: NavItem[] = [
  { label: 'Executive Overview', shortLabel: 'Overview', path: '/overview', icon: LayoutDashboard, group: 'control' },
  { label: 'Regulatory Radar', shortLabel: 'Radar', path: '/radar', icon: Activity, count: 9, group: 'control' },
  { label: 'Reporting Inventory', shortLabel: 'Inventory', path: '/inventory', icon: ClipboardList, group: 'operate' },
  { label: 'Data & Lineage', shortLabel: 'Lineage', path: '/lineage', icon: GitBranch, group: 'operate' },
  { label: 'Mapping Studio', shortLabel: 'Mapping', path: '/mapping', icon: Blocks, count: 2, group: 'operate' },
  { label: 'Validation', shortLabel: 'Validation', path: '/validation', icon: CheckCheck, count: 6, group: 'operate' },
  { label: 'Cycles & Filings', shortLabel: 'Cycles', path: '/cycles', icon: CalendarClock, group: 'operate' },
  { label: 'Exceptions', shortLabel: 'Exceptions', path: '/exceptions', icon: ShieldAlert, count: 4, group: 'assure' },
  { label: 'Evidence', shortLabel: 'Evidence', path: '/evidence', icon: FileArchive, group: 'assure' },
  { label: 'Executive Reports', shortLabel: 'Reports', path: '/reports', icon: ChartNoAxesCombined, group: 'assure' },
  { label: 'Administration', shortLabel: 'Admin', path: '/settings', icon: Settings2, group: 'assure' },
];

const pageTitles = Object.fromEntries(navItems.map((item) => [item.path, item.label]));

function ProductMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cx('product-mark', compact && 'product-mark-compact')}>
      <span className="product-icon" aria-hidden="true"><FileCheck2 size={22} /></span>
      {!compact ? <span><strong>RegOps</strong><small>Reporting Analyst</small></span> : null}
    </div>
  );
}

function SuiteSwitcher({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="suite-switcher-wrap">
      <button className={cx('suite-switcher', collapsed && 'suite-switcher-collapsed')} onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-haspopup="menu">
        <ProductMark compact={collapsed} />
        {!collapsed ? <ChevronDown size={15} aria-hidden="true" /> : null}
      </button>
      {open ? (
        <div className={cx('suite-menu', collapsed && 'suite-menu-collapsed')} role="menu">
          <p className="menu-label">Audit-Ready AI Suite</p>
          <button role="menuitem" className="suite-option suite-option-active" onClick={() => setOpen(false)}>
            <span className="suite-option-icon"><FileCheck2 size={18} /></span>
            <span><strong>RegOps Reporting Analyst</strong><small>Regulatory Reporting Control Plane</small></span>
            <CheckCheck size={16} />
          </button>
          <a role="menuitem" className="suite-option" href="https://mrm-quant-console.audit-ready-ai.com/" target="_blank" rel="noreferrer">
            <span className="suite-option-icon suite-mrm"><Boxes size={18} /></span>
            <span><strong>MRM Quant Analyst</strong><small>AI & Model Risk Operating System</small></span>
          </a>
          <p className="suite-note">Der Wechsel öffnet das zweite Produkt in einem neuen Tab.</p>
        </div>
      ) : null}
    </div>
  );
}

function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }: { collapsed: boolean; setCollapsed: (value: boolean) => void; mobileOpen: boolean; setMobileOpen: (value: boolean) => void }) {
  let lastGroup: NavItem['group'] | undefined;
  const groupLabels = { control: 'Steuern', operate: 'Produzieren', assure: 'Nachweisen' };
  return (
    <>
      {mobileOpen ? <button className="mobile-scrim" onClick={() => setMobileOpen(false)} aria-label="Navigation schließen" /> : null}
      <aside className={cx('sidebar', collapsed && 'sidebar-collapsed', mobileOpen && 'sidebar-mobile-open')}>
        <div className="sidebar-head">
          <SuiteSwitcher collapsed={collapsed} />
          <button className="mobile-close icon-button" onClick={() => setMobileOpen(false)} aria-label="Navigation schließen"><X size={20} /></button>
        </div>
        <nav className="main-nav" aria-label="Hauptnavigation">
          {navItems.map((item) => {
            const showGroup = lastGroup !== item.group;
            lastGroup = item.group;
            const Icon = item.icon;
            return (
              <div key={item.path} className="nav-block">
                {showGroup && !collapsed ? <p className="nav-group">{groupLabels[item.group ?? 'control']}</p> : null}
                {showGroup && collapsed ? <span className="nav-separator" /> : null}
                <NavLink to={item.path} onClick={() => setMobileOpen(false)} className={({ isActive }) => cx('nav-item', isActive && 'nav-item-active')} title={collapsed ? item.label : undefined}>
                  <Icon size={18} strokeWidth={1.9} />
                  {!collapsed ? <span>{item.label}</span> : null}
                  {!collapsed && item.count ? <span className="nav-count">{item.count}</span> : null}
                </NavLink>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {!collapsed ? (
            <div className="help-card">
              <span><CircleHelp size={17} /></span>
              <div><strong>RegOps Guide</strong><small>Workflows & Kontrollen</small></div>
              <ChevronLeft size={14} className="rotate-180" />
            </div>
          ) : null}
          <button className="collapse-button" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? 'Navigation erweitern' : 'Navigation einklappen'}>
            {collapsed ? <PanelLeftOpen size={18} /> : <><PanelLeftClose size={18} /><span>Navigation einklappen</span></>}
          </button>
        </div>
      </aside>
    </>
  );
}

function CommandSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { data } = useWorkspace();
  const results = useMemo(() => {
    if (!query.trim()) return navItems.slice(0, 6).map((item) => ({ id: item.path, label: item.label, detail: 'Navigation', path: item.path, icon: item.icon }));
    const value = query.toLowerCase();
    const navigation = navItems.filter((item) => item.label.toLowerCase().includes(value)).map((item) => ({ id: item.path, label: item.label, detail: 'Navigation', path: item.path, icon: item.icon }));
    const reports = (data?.reports ?? []).filter((report) => `${report.name} ${report.code} ${report.regime}`.toLowerCase().includes(value)).map((report) => ({ id: report.id, label: report.name, detail: `${report.code} · ${report.regime}`, path: `/inventory?report=${report.id}`, icon: ClipboardList }));
    return [...navigation, ...reports].slice(0, 8);
  }, [data?.reports, query]);
  const choose = (path: string) => { navigate(path); onClose(); setQuery(''); };
  return (
    <Modal open={open} onClose={onClose} title="Arbeitsbereich durchsuchen" description="Navigieren Sie zu Reports, Regimes und Modulen." size="large">
      <div className="command-search">
        <Search size={19} aria-hidden="true" />
        <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Report, Regime oder Modul suchen …" aria-label="Globale Suche" />
        <kbd>ESC</kbd>
      </div>
      <div className="command-results">
        <p className="menu-label">{query ? `${results.length} Ergebnisse` : 'Schnellzugriff'}</p>
        {results.map((result) => {
          const Icon = result.icon;
          return <button key={result.id} onClick={() => choose(result.path)}><span><Icon size={17} /></span><span><strong>{result.label}</strong><small>{result.detail}</small></span><ChevronLeft className="rotate-180" size={15} /></button>;
        })}
        {!results.length ? <div className="command-empty">Keine passenden Einträge gefunden.</div> : null}
      </div>
    </Modal>
  );
}

function AppHeader({ onMobileMenu }: { onMobileMenu: () => void }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { data, refresh, status } = useWorkspace();
  const location = useLocation();
  const title = pageTitles[location.pathname] ?? 'RegOps Reporting Analyst';
  return (
    <header className="app-header">
      <div className="header-left">
        <button className="mobile-menu icon-button" onClick={onMobileMenu} aria-label="Navigation öffnen"><Menu size={20} /></button>
        <div className="header-title"><span>Workspace</span><strong>{title}</strong></div>
      </div>
      <div className="header-actions">
        <button className="global-search" onClick={() => setSearchOpen(true)} aria-label="Suche öffnen"><Search size={16} /><span>Suche</span><kbd>⌘ K</kbd></button>
        <button className="icon-button" onClick={() => void refresh()} aria-label="Daten aktualisieren" disabled={status === 'loading'}><RefreshCw className={status === 'loading' ? 'spin' : ''} size={18} /></button>
        <div className="notification-wrap">
          <button className="icon-button notification-button" onClick={() => setNotificationsOpen((value) => !value)} aria-label="Benachrichtigungen" aria-expanded={notificationsOpen}><Bell size={18} /><span /></button>
          {notificationsOpen ? (
            <div className="notification-menu">
              <div className="popover-header"><strong>Benachrichtigungen</strong><button onClick={() => setNotificationsOpen(false)}>Alle gelesen</button></div>
              <div className="notification-item"><span className="notification-icon warning"><ShieldAlert size={15} /></span><div><strong>4 Blocker im DORA-Zyklus</strong><p>Validierung um 08:43 abgeschlossen.</p><small>vor 6 Min.</small></div></div>
              <div className="notification-item"><span className="notification-icon info"><Activity size={15} /></span><div><strong>Regulatory Impact zur Prüfung</strong><p>REG-2048 betrifft 17 Objekte.</p><small>vor 42 Min.</small></div></div>
              <div className="notification-item"><span className="notification-icon success"><CheckCheck size={15} /></span><div><strong>Evidence Pack vollständig</strong><p>COREP Q2 besitzt 100 % Evidenz.</p><small>gestern</small></div></div>
            </div>
          ) : null}
        </div>
        <div className="header-divider" />
        <button className="profile-button"><Avatar name="Leonie Berg" /><span><strong>Leonie Berg</strong><small>{data?.tenant.role ?? 'Reporting Lead'}</small></span><ChevronDown size={14} /></button>
      </div>
      <CommandSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}

function EnvironmentBanner() {
  const { data } = useWorkspace();
  if (!data || data.meta.source === 'api') return null;
  const hybrid = data.meta.source === 'api-hybrid';
  return (
    <div className="environment-banner" role="status">
      <span className="demo-pill"><Sparkles size={13} /> DEMO</span>
      <span><strong>{hybrid ? 'Hybrid-Demo mit API-Systemstatus' : 'Synthetischer Workspace'}</strong> – {hybrid ? 'Detaildaten bleiben synthetisch' : 'keine produktiven Meldedaten oder Behördenverbindung'}.</span>
      <span className="environment-time">Aktualisiert {formatDate(data.meta.generatedAt, true)}</span>
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { status, error, refresh } = useWorkspace();
  return (
    <div className={cx('app-shell', collapsed && 'shell-collapsed')}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div className="app-stage">
        <AppHeader onMobileMenu={() => setMobileOpen(true)} />
        <EnvironmentBanner />
        <main className="page-content" id="main-content">
          {status === 'loading' ? <LoadingState fullPage /> : null}
          {status === 'error' ? <ErrorState description={error ?? undefined} onRetry={() => void refresh()} /> : null}
          {status === 'forbidden' ? <PermissionState scope="den ausgewählten Tenant" /> : null}
          {status === 'ready' ? children : null}
        </main>
        <footer className="app-footer"><span>RegOps Reporting Analyst · Control Plane</span><span>Demo Workspace · Build 0.1.0</span></footer>
      </div>
    </div>
  );
}
